import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const looksLikeEmail = (s) => /@/.test(s || "");

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("login"); // login | register

  // login form
  const [identifier, setIdentifier] = useState(""); // email or mobile
  const [loginPassword, setLoginPassword] = useState("");

  // register form
  const [name, setName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regMobile, setRegMobile] = useState("");
  const [regPassword, setRegPassword] = useState("");

  const postLoginPath = () => {
    try {
      const r = sessionStorage.getItem("mb_post_login");
      if (r) { sessionStorage.removeItem("mb_post_login"); return r; }
    } catch (_) { /* ignore */ }
    return "/app";
  };

  useEffect(() => { if (user) navigate(postLoginPath(), { replace: true }); }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const id = looksLikeEmail(identifier) ? identifier.trim().toLowerCase() : onlyDigits(identifier);
      if (!id) { toast.error("Enter your email or mobile number"); setLoading(false); return; }
      await login(id, loginPassword);
      toast.success("Welcome back!");
      // Redirect handled by the user-effect above (honors mb_post_login once).
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!name.trim()) { toast.error("Please enter your name"); setLoading(false); return; }
      const mob = onlyDigits(regMobile);
      if (mob.length !== 10) { toast.error("Enter a 10-digit Indian mobile number"); setLoading(false); return; }
      const payload = { name: name.trim(), password: regPassword, mobile: mob };
      if (regEmail.trim()) payload.email = regEmail.trim().toLowerCase();
      await register(payload);
      toast.success("Account created — 500 welcome credits added 🌱");
      // Redirect handled by the user-effect above (honors mb_post_login once).
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  const handleGoogle = () => {
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" style={{ background: "#FAF9F6", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div className="hidden md:flex flex-col justify-between p-10" style={{ background: "linear-gradient(135deg, #FFF4E4 0%, #FDE7C8 80%)" }}>
        <div className="flex items-center gap-2">
          <img src="/mapbazar-logo.png" alt="Mapbazar" style={{ height: 38, width: "auto", display: "block" }} data-testid="login-brand-logo" />
        </div>
        <div className="space-y-5 max-w-md">
          <span className="inline-block" style={{ padding: "5px 12px", borderRadius: 100, background: "#0F3D2E", color: "white", fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase" }}>
            🤖 Two AI agents · One local economy
          </span>
          <h1 className="text-4xl font-bold leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "#1A1A1A" }}>
            Local AI for local shops,{" "}
            <em style={{ fontStyle: "italic", color: "#0F3D2E" }}>local AI for local shoppers.</em>
          </h1>
          <p className="text-base leading-relaxed" style={{ color: "#5C3D12" }}>
            <strong>ShopKeeper AI</strong> lists every kirana product in seconds — no typing, no photo editing.
            <strong> Buyer AI</strong> searches inside neighborhood shops, finds your stock, and brings it to your door in 30 minutes.
          </p>
          <div className="grid gap-3 pt-2">
            <div className="flex items-start gap-3 bg-white/75 backdrop-blur rounded-2xl p-4" style={{ border: "1px solid rgba(15,61,46,0.18)" }}>
              <div className="text-xl mt-0.5">🧙‍♂️</div>
              <div>
                <div className="font-semibold" style={{ color: "#1A1A1A" }}>AI agent for shopkeepers</div>
                <div className="text-sm" style={{ color: "#666" }}>Snap a photo — ShopKeeper AI writes the listing, polishes the image and pins your shop on the map in seconds.</div>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-white/75 backdrop-blur rounded-2xl p-4" style={{ border: "1px solid rgba(201,123,60,0.22)" }}>
              <div className="text-xl mt-0.5">🛵</div>
              <div>
                <div className="font-semibold" style={{ color: "#1A1A1A" }}>AI agent for buyers</div>
                <div className="text-sm" style={{ color: "#666" }}>Search real-time stock inside local shops, order in one tap, get it delivered in 30 minutes — and earn HealthCoin on every purchase.</div>
              </div>
            </div>
          </div>
          <div className="text-xs leading-relaxed" style={{ color: "#5C3D12", opacity: 0.85, paddingTop: 4 }}>
            <strong>Billions of dollars of offline inventory</strong> have been invisible to the internet because shopkeepers don't type, write listings or edit photos. AI changes that on one click.
          </div>
        </div>
        <div className="text-xs" style={{ color: "#8C5A1C", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span>© Mapbazar. Local commerce, unlocked by AI.</span>
          <a href="/why-now" data-testid="login-why-now-link" style={{ color: "#0F3D2E", textDecoration: "none", fontWeight: 700 }}>Why Now? →</a>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <Card className="w-full max-w-md rounded-2xl shadow-sm" style={{ borderColor: "#EAE8DC" }}>
          <CardContent className="p-6 md:p-8 space-y-5">
            <div className="md:hidden flex items-center gap-2">
              <img src="/mapbazar-logo.png" alt="Mapbazar" style={{ height: 30, width: "auto", display: "block" }} data-testid="login-brand-logo-mobile" />
            </div>
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "#1A1A1A" }}>
                {tab === "login" ? "Welcome back" : "Join the local AI revolution"}
              </h2>
              <p className="text-sm mt-1" style={{ color: "#666" }}>
                {tab === "login"
                  ? "Sign in with email or mobile."
                  : "Shopkeeper or shopper — one account works for both. Our AI lists local stock, our AI finds it for buyers, all in 30 minutes."}
              </p>
            </div>

            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList className="grid grid-cols-2 w-full rounded-xl">
                <TabsTrigger value="login" data-testid="tab-login">Sign in</TabsTrigger>
                <TabsTrigger value="register" data-testid="tab-register">Sign up</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="pt-4">
                <form onSubmit={handleLogin} className="space-y-3.5">
                  <Field label="Email or mobile number" id="login-id">
                    <Input
                      id="login-id"
                      data-testid="login-identifier-input"
                      type="text"
                      autoComplete="username"
                      placeholder="you@example.com or 9876543210"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      required
                      className="rounded-xl h-11"
                    />
                  </Field>
                  <Field label="Password" id="login-password">
                    <Input id="login-password" data-testid="login-password-input" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required className="rounded-xl h-11" />
                  </Field>
                  <Button data-testid="login-submit-btn" disabled={loading} type="submit" className="w-full rounded-xl h-11 text-white hover:opacity-90" style={{ background: "#0F3D2E" }}>
                    {loading ? "Signing in…" : "Sign in"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register" className="pt-4">
                <form onSubmit={handleRegister} className="space-y-3.5">
                  <Field label="Your name" id="reg-name">
                    <Input id="reg-name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="rounded-xl h-11" />
                  </Field>

                  <Field label="Mobile number (required)" id="reg-mobile">
                    <Input
                      id="reg-mobile"
                      data-testid="register-mobile-input"
                      type="tel"
                      inputMode="numeric"
                      placeholder="9876543210"
                      value={regMobile}
                      onChange={(e) => setRegMobile(e.target.value)}
                      required
                      maxLength={10}
                      className="rounded-xl h-11"
                    />
                  </Field>

                  <Field label="Email (optional)" id="reg-email">
                    <Input id="reg-email" data-testid="register-email-input" type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} className="rounded-xl h-11" />
                  </Field>

                  <Field label="Password" id="reg-password">
                    <Input id="reg-password" data-testid="register-password-input" type="password" minLength={6} value={regPassword} onChange={(e) => setRegPassword(e.target.value)} required className="rounded-xl h-11" />
                  </Field>
                  <Button data-testid="register-submit-btn" disabled={loading} type="submit" className="w-full rounded-xl h-11 text-white hover:opacity-90" style={{ background: "#0F3D2E" }}>
                    {loading ? "Creating…" : "Create account · 500 credits 🌱"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-slate-400 uppercase tracking-wider">or</span></div>
            </div>

            <Button data-testid="google-signin-btn" onClick={handleGoogle} variant="outline" className="w-full rounded-xl h-11 gap-2 border-slate-200">
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5h-1.8V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 18.9 13 24 13c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.3 2.4-5.2 0-9.6-3.3-11.2-8L6.2 32.6C9.4 39.4 16.1 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5h-1.8V20H24v8h11.3c-.7 2-2 3.8-3.7 5.2l6.2 5.2C40.9 36 44 30.5 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
              Continue with Google
            </Button>

            <p className="text-xs text-slate-400 text-center">
              Demo: <span className="font-mono">demo@ibloom.com / demo1234</span>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const Field = ({ label, id, children }) => (
  <div className="space-y-1.5"><Label htmlFor={id} className="text-slate-700">{label}</Label>{children}</div>
);
